import React from "react";
import { HtmlTooltip, StyledDarkRedInformationTypography } from "../../../components/commonComponents/StyledComponents/styled";
import { Box, InputAdornment, Stack, Typography } from "@mui/material";
import ChipBasedTextField from "../../../components/commonComponents/TextInputs/ChipBasedTextField";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../Store/actions";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { checkListings } from "../../../api_calls/listingCheck";
import { CustomTextInput } from "../../../components/commonComponents/TextInputs/style";
import { validateEmpty, validateNumbers } from "../../../api_calls/utils";
import { DisabledGreyButton } from "../../../components/commonComponents/Buttons/DisabledButton";
import { InfoOutlined } from "@mui/icons-material";
import ListingCheckStoresList from "../ModalContent/ListingCheckStoresList";
import CustomizedHierarchicalMultiselect from "../../../components/commonComponents/Selectors/CustomizedHierarchicalMultiselect";
import Results from "./Results";

const ListingCheckUi = ({ analysisGroupOptions, usedFor, campaignRecord }) => {
    const userStateDetails = useSelector((state) => state.userSelections);
    const dispatchAction = useDispatch();
    const [fieldErrors, setFieldErrors] = React.useState({});

    const showRequestCancelButton = (showHideFlag) => {
        dispatchAction(updateStateData({
            show_loading_cancel_button: showHideFlag,
        }));
    }

    const onGtinChange = (selectedOptions) => {
        // Handle GTIN changes here
        dispatchAction(updateStateData({
            listing_check_selected_gtin: selectedOptions,
        }));
    };

    const validateInputs = () => {
        let isValid = true;
        let errorMessages = {};
        // gtin(s) selected for listing check
        const gtinList = typeof userStateDetails.listing_check_selected_gtin !== "undefined" ? userStateDetails.listing_check_selected_gtin : [];
        // validate gtin list
        const validateGtinList = validateEmpty(gtinList, true);
        // gtin(s) must be entered
        if (validateGtinList.error === true) {
            isValid = false;
            errorMessages = {
                ...errorMessages,
                gtin_list: "Please enter at least one GTIN"
            };
        } else {
            // validate each gtin for digits only
            gtinList.forEach((gtin) => {
                const validateGtinValue = validateNumbers(gtin);
                if (validateGtinValue.error === true) {
                    isValid = false;
                    errorMessages = {
                        ...errorMessages,
                        gtin_list: "GTINs must contain only digits"
                    };
                }
            });
        }
        // listing check period value
        const periodValue = typeof userStateDetails.listing_check_period !== "undefined" ? userStateDetails.listing_check_period : "";
        // validate period value
        const validatePeriodValue = validateNumbers(periodValue);

        // period must be entered and be a positive integer
        if (validatePeriodValue.error === true) {
            isValid = false;
            errorMessages = {
                ...errorMessages,
                listing_check_period: "Please enter a valid positive integer for the period"
            };
        } else {
            if (periodValue <= 0) {
                isValid = false;
                errorMessages = {
                    ...errorMessages,
                    listing_check_period: "Please enter a period value greater than 0"
                };
            }
            if (periodValue > 0 && periodValue > 12) {
                isValid = false;
                errorMessages = {
                    ...errorMessages,
                    listing_check_period: "Please enter a period value less than or equal to 12"
                };
            }
        }
        return { isValid, errorMessages };
    }

    return (
        <>
            <Typography variant="body2">
                {
                    usedFor === "withSavedListingCheck" ?
                        "Perform a new Listing Check for this Campaign. Saving a new Listing Check will permanently overwrite any previously saved Listing Check."
                        :
                        typeof campaignRecord !== "undefined" ?
                            "Use the Listing Check to verify in how many of your stores the respective products are available for sale. The check is based on GTINs across all stores available for this campaign."
                            :
                            "Use the Listing Check to verify in how many of your stores the respective products are available for sale. The check is based on GTINs across all your stores. Campaign-specific Listing Checks can be performed in the Analytics Products section of a Contact-based Campaign."
                }

            </Typography>
            <Box height={20} />
            {/* ---------- enter gtin field --------- */}
            {
                typeof analysisGroupOptions !== "undefined" && analysisGroupOptions.length > 0 ? (
                    <CustomizedHierarchicalMultiselect
                        label={<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            Select GTIN(s)*
                            <HtmlTooltip title={
                                <Box width={300}>
                                    <Typography variant="body2">
                                        Select all GTINs you want to check. You can only select GTINs that have already been added as analytics products for this campaign.
                                    </Typography>
                                    <Box height={10} />
                                    <Typography variant="body2">
                                        As store assortments change over time, results are subject to change. Results are determined by the entered GTINs and may reflect a different product or show no availability if a GTIN was entered incorrectly.
                                    </Typography>
                                    <Box height={10} />
                                    <Typography variant="body2">
                                        Depending on the number of products, the check may take some time. Do not close CAP Explorer while the process is running.
                                    </Typography>
                                </Box>
                            }>
                                <InfoOutlined
                                    // fontSize="small"
                                    sx={{ color: "#AF3241", cursor: "pointer", fontSize: "16px" }}
                                />
                            </HtmlTooltip>
                        </Box>}
                        categories={analysisGroupOptions}
                        value={typeof userStateDetails.listing_check_selected_gtin !== "undefined" ? userStateDetails.listing_check_selected_gtin : []}
                        onChange={onGtinChange}
                        fieldErrors={typeof fieldErrors !== "undefined" && typeof fieldErrors.gtin_list !== "undefined" ? fieldErrors.gtin_list : ""}
                    />
                ) : (
                    <ChipBasedTextField
                        label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                Enter GTIN(s)*
                                <HtmlTooltip title={
                                    <Box width={300}>
                                        <Typography variant="body2">
                                            Press the return key to add a GTIN to the list. As store assortments change over time, results are subject to change. Results are determined by GTINs and may reflect a different product or show no availability if a GTIN is entered incorrectly.
                                        </Typography>
                                        <Box height={10} />
                                        <Typography variant="body2">
                                            Depending on the number of products, the check may take some time. Do not close CAP Explorer during loading.
                                        </Typography>
                                    </Box>
                                }>
                                    <InfoOutlined
                                        // fontSize="small"
                                        sx={{ color: "#AF3241", cursor: "pointer", fontSize: "16px" }}
                                    />
                                </HtmlTooltip>
                            </Box>
                        }
                        id="enterGtinTextInput"
                        values={typeof userStateDetails.listing_check_selected_gtin !== "undefined" ? userStateDetails.listing_check_selected_gtin : []}
                        onChangeCall={onGtinChange}
                        fieldErrors={typeof fieldErrors !== "undefined" && typeof fieldErrors.gtin_list !== "undefined" ? fieldErrors.gtin_list : ""}
                    />
                )

            }

            <Box height={10} />
            <Box width={200}>
                <CustomTextInput
                    label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            Check Period*
                            <HtmlTooltip title={
                                <Box width={300}>
                                    <Typography variant="body2">
                                        The Listing Check verifies whether the GTINs were sold in the selected number of past weeks in each individual store. You can enter a check period between 1 and 12 weeks.
                                    </Typography>
                                </Box>
                            }>
                                <InfoOutlined
                                    // fontSize="small"
                                    sx={{ color: "#AF3241", cursor: "pointer", fontSize: "16px" }}
                                />
                            </HtmlTooltip>
                        </Box>
                    }
                    elemId="listingCheckPeriodValue"
                    fieldName="listingCheckPeriodValue"
                    defaultVal={typeof userStateDetails.listing_check_period !== "undefined" ? userStateDetails.listing_check_period : ""}
                    onChange={(e) => {
                        dispatchAction(updateStateData({
                            listing_check_period: e.target.value,
                        }));
                    }}
                    inputType="text"
                    required={false}
                    variant="standard"
                    autoComplete="off"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <StyledDarkRedInformationTypography variant="body2" sx={{ fontWeight: "normal" }}>
                                    week(s)
                                </StyledDarkRedInformationTypography>
                            </InputAdornment>
                        ),
                    }}
                    error={typeof fieldErrors !== "undefined" && typeof fieldErrors.listing_check_period !== "undefined" ? true : false}
                    helperText={typeof fieldErrors !== "undefined" && typeof fieldErrors.listing_check_period !== "undefined" ? fieldErrors.listing_check_period : ""}
                />
            </Box>
            <Box height={20} />

            <Stack direction="row" spacing={2}>
                {
                    typeof userStateDetails.listing_check_selected_gtin !== "undefined" && userStateDetails.listing_check_selected_gtin.length > 0 ? (
                        <ActionButton
                            onClick={(e) => {
                                e.preventDefault();
                                const validate = validateInputs();
                                if (validate.isValid === true) {
                                    setFieldErrors({});
                                    showRequestCancelButton(true);
                                    checkListings().then(() => {
                                        dispatchAction(updateStateData({
                                            checked_gtin_values: typeof userStateDetails.listing_check_selected_gtin !== "undefined" ? userStateDetails.listing_check_selected_gtin : [],
                                            checked_period_value: typeof userStateDetails.listing_check_period !== "undefined" ? userStateDetails.listing_check_period : "",
                                        })
                                        );
                                        showRequestCancelButton(false);
                                    });
                                } else {
                                    showRequestCancelButton(false);
                                    setFieldErrors(validate.errorMessages);
                                }
                            }}
                            label={"Check"}
                            icon={<></>}
                        />
                    ) : (
                        <DisabledGreyButton
                            onClick={(e) => {
                                e.preventDefault();
                            }}
                            label="Check"
                            icon={<></>}
                        />
                    )
                }
            </Stack>
            <Box height={30} />
            {
                typeof userStateDetails.listing_check_response_data !== "undefined" &&
                <Results
                    reduxKeyToLoadResults={"listing_check_response_data"}
                    reduxKeyToCheckSortedProducts={"sorted_product_list_data"}
                    resultsTitle={"Results"}
                    campaignRecord={campaignRecord}
                />
            }
        </>
    );
};

export default ListingCheckUi;
